import { useEffect, useId, useRef, type ReactNode } from "react";

import { Button } from "@/components/ui/button";

/**
 * Shared confirmation modal. Purely presentational — the caller owns the
 * mutation and passes `onConfirm`, `isPending`, and (optionally) `error` down.
 * Replaces five byte-for-byte-identical hand-rolled shells across the admin UI
 * (test/tag/job delete + the test-from-job unlink).
 *
 * Body is paragraphs ONLY — this component owns the wrapper div, so callers
 * pass just the `<p>`s (use a fragment for multiple).
 */
export interface ConfirmDialogProps {
  title: ReactNode;
  body: ReactNode;
  confirmLabel: string;
  pendingLabel?: string;
  /** The dismiss button's words; "Cancel" unless that would read like the action. */
  cancelLabel?: string;
  confirmVariant?: "default" | "destructive";
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
  error?: string | null;
  /**
   * Move focus to the dismiss button on open and give it back to whatever had
   * it on close (skipped when that element has left the page). Opt-in so the
   * admin dialogs keep their behaviour; the candidate's submit confirm needs
   * it — without it a keyboard user's first Tab lands under the overlay and a
   * screen reader is never told the dialog opened. Dismiss, never confirm: an
   * Enter meant for the button behind must not submit anything.
   */
  focusCancelOnOpen?: boolean;
}

export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  pendingLabel,
  cancelLabel = "Cancel",
  confirmVariant = "destructive",
  onConfirm,
  onCancel,
  isPending,
  error,
  focusCancelOnOpen = false,
}: ConfirmDialogProps) {
  const titleId = useId();
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!focusCancelOnOpen) return;
    const previous = document.activeElement;
    cancelRef.current?.focus();
    return () => {
      if (previous instanceof HTMLElement && previous.isConnected) {
        previous.focus();
      }
    };
  }, [focusCancelOnOpen]);

  // Close on Escape — small a11y win for a modal that grabs the cursor.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isPending) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, isPending]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={() => !isPending && onCancel()}
    >
      <div
        className="max-h-[calc(100vh-2rem)] w-full max-w-md overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* break-words: a long address or unbroken name is what the person is
            confirming — it wraps, never scrolls sideways (verify 2026-10-30). */}
        <h2 id={titleId} className="break-words text-lg font-semibold tracking-tight">
          {title}
        </h2>
        <div className="mt-3 space-y-2 break-words text-sm text-muted-foreground">{body}</div>
        {error && (
          <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <Button
            ref={cancelRef}
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isPending}
            className="cursor-pointer"
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={confirmVariant}
            onClick={onConfirm}
            disabled={isPending}
            className="cursor-pointer"
          >
            {isPending ? (pendingLabel ?? confirmLabel) : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
